import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, MiniBoard, SplitButton, StatusPill, Text } from "components";
import {
  getDisplayStatus,
  computeCompletion,
  computePlayProgress,
  PUBLISH_STATUS,
  DISPLAY_STATUS_AWAITING,
} from "../../lifecycle";
import { boardCategories, metaParts, touchedAgo } from "../dashboardUtils";
import { PlayIcon, CheckIcon, PencilIcon, PlusIcon } from "./icons";
import { CREATE_CHOOSER, CREATE_BLANK, CREATE_AI } from "./createEntries";

const PRIMARY_ICON = {
  play: <PlayIcon size={14} />,
  check: <CheckIcon />,
  pencil: <PencilIcon />,
};

/** Eyebrow + meta copy for the active game's hero, by lifecycle status. */
const heroCopy = (game) => {
  const status = getDisplayStatus(game);
  const { roundsLabel, written, total } = metaParts(game);
  const { percentage } = computeCompletion(game);
  const ago = touchedAgo(game);

  switch (status) {
    case PUBLISH_STATUS.IN_PROGRESS: {
      const { played, total: playTotal } = computePlayProgress(game);
      return {
        eyebrow: "Game in progress",
        meta: `Started ${ago} · ${played}/${playTotal} clues played`,
      };
    }
    case PUBLISH_STATUS.READY:
      return {
        eyebrow: "Ready to launch",
        meta: `Marked ready ${ago} · ${roundsLabel} · ${written} clues`,
      };
    case DISPLAY_STATUS_AWAITING:
      return {
        eyebrow: "All clues written — ready to publish?",
        meta: `${written} clues written · last edit ${ago}`,
      };
    default: // draft < 100%
      return {
        eyebrow: "In progress",
        meta: `${written}/${total} clues · ${percentage}% complete · last edit ${ago}`,
      };
  }
};

/** §5.2.e — no draft/ready/in-progress game to surface. */
const EmptyHero = ({ counts }) => {
  const navigate = useNavigate();
  const returning = (counts?.played ?? 0) > 0;
  return (
    <Card spotlight padding="lg" className="da-hero da-hero--empty">
      <div className="da-hero-body">
        <Text variant="eyebrow" tone="accent" className="da-hero-eyebrow">
          {returning ? "Your stage is clear" : "Welcome to Groupardy"}
        </Text>
        <Text variant="h1" className="da-hero-title">
          {returning ? "Build your next show" : "Create your first game"}
        </Text>
        <Text variant="body" tone="muted" className="da-hero-meta">
          {returning
            ? `You've wrapped ${counts.played} ${counts.played === 1 ? "game" : "games"}. Spin up the next one.`
            : "Write your own categories and clues, or let AI draft a full board in seconds."}
        </Text>
        <div className="da-hero-actions">
          <SplitButton
            variant="primary"
            size="lg"
            startIcon={<PlusIcon />}
            onClick={() => navigate(CREATE_CHOOSER)}
            items={[
              { label: "＋  Blank", onClick: () => navigate(CREATE_BLANK) },
              { label: "✨  AI generate", onClick: () => navigate(CREATE_AI) },
            ]}
          >
            New Game
          </SplitButton>
        </div>
      </div>
    </Card>
  );
};

/**
 * The single most-relevant game with a status-appropriate primary CTA (PRD §5).
 * One slot, five states. `actions` is the useGameActions result for `game`.
 */
const HeroCard = ({ game, actions, counts }) => {
  const navigate = useNavigate();
  if (!game) return <EmptyHero counts={counts} />;

  const { eyebrow, meta } = heroCopy(game);
  const primary = actions?.primary;
  const displayStatus = getDisplayStatus(game);
  const launches = primary?.icon === "play";
  // The hero body (board + text) opens Game Home, matching the grid cards /
  // queue rows. The CTA column is excluded so its buttons act on their own.
  const goHome = () => navigate(`/manage/game/${game.id}`);
  const heroItems = [{ label: "Game home", onClick: goHome }, ...(actions?.menuItems || [])];

  return (
    <Card spotlight padding="lg" className="da-hero">
      <div className="da-hero-thumb da-hero-link" onClick={goHome}>
        <MiniBoard categories={boardCategories(game, 4)} values={[200, 400, 600]} />
      </div>
      <div
        className="da-hero-body da-hero-link"
        onClick={goHome}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") goHome();
        }}
      >
        <Text variant="eyebrow" tone="accent" className="da-hero-eyebrow">
          {eyebrow}
        </Text>
        <div className="da-hero-titlerow">
          <Text variant="h1" className="da-hero-title">
            {game.name || "Untitled game"}
          </Text>
          <StatusPill status={displayStatus} />
        </div>
        <Text variant="body" tone="muted" className="da-hero-meta">
          {meta}
        </Text>
      </div>
      {primary && (
        <div className="da-hero-cta">
          <SplitButton
            variant="primary"
            size="lg"
            startIcon={PRIMARY_ICON[primary.icon]}
            onClick={primary.onClick}
            items={heroItems}
          >
            {primary.label}
          </SplitButton>
          {launches && (
            <Text variant="caption" tone="muted" className="da-hero-hint">
              Opens the game board in a new tab.
            </Text>
          )}
        </div>
      )}
    </Card>
  );
};

export default HeroCard;
